import { HotelProduct } from '@/entities/hotel/@/product';
import { HOTEL_CATEGORIES } from '@/entities/hotelCategory/@/product';
import { MEALS } from '@/entities/meal/@/product';
import { type SearchCriteria } from '@/entities/searchCriteria/@/product';
import { getCustomerProfile } from '@/shared/api/getCustomerProfile';
import {
  createProductSearchSession,
  fetchProductSearchCache,
  getProductSearchSessionStatus,
  type ProductSearchCacheParams,
  type ProductSearchCacheResult,
  type ProductSearchSession,
  type ProductSearchSessionStatus,
} from '@/shared/api/productSearch';
import { type AbTest, abTests } from '@/shared/lib/AbTests';
import { sleep } from '@/shared/lib/sleep';
import { getTrSm } from '@/shared/lib/trSm';
import type { EmitArgs, TypedEventEmitter } from '@/shared/lib/TypedEventEmitter';
import type { Country, CountryProductDTO, DepartureCity, Resort } from '@/shared/model';
import type {
  Product,
  ProductDTO,
  ProductDictionaries,
  ProductSearchBatch,
  ProductSearchConfig,
  ProductSearchEndpoints,
  ProductSearchError,
  ProductSearchErrorPhase,
  ProductSearchEvents,
  ProductSearchMode,
  ProductSearchOptions,
  ProductSearchPhase,
  ProductSearchProgress,
  ProductSearchRequest,
  ProductSearchResult,
  ProductSearchSnapshot,
  ProductSearchState,
  ProductSearchTimings,
} from './types';
import {
  SEARCH_MIDDLE_REQUEST_LIMIT,
  SEARCH_STATUS_POLL_INTERVAL_MS,
  SEARCH_STATUS_WAITING_TIME_MS,
} from '../config/search';
import { buildProductSearchRequest } from '../lib/buildProductSearchRequest';
import { getProductKey } from '../lib/getProductKey';

const SYNC_MODES = new Set<ProductSearchMode>(['both', 'sync']);
const ASYNC_MODES = new Set<ProductSearchMode>(['both', 'async', 'asyncWithoutProducts']);

interface ProductEntry<P extends Product> {
  key: string;
  product: P;
}

interface SearchRequestMeta {
  customerUuid: string | null;
  abTests: AbTest[] | null;
  trSm?: string;
}

interface SearchRunParams<P extends Product, DTO extends ProductDTO> {
  emitter: TypedEventEmitter<ProductSearchEvents<P>>;
  config: ProductSearchConfig<P, DTO>;
  criteria: SearchCriteria;
  options: ProductSearchOptions;
  searchUuid: string;
  clientUuid: string;
}

// ApiService маппит отмену axios в AbortError из utils, тесты и fetch кидают DOMException — сверяем по name.
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function indexById<T extends { id: number | string }>(items: readonly T[]): Record<string, T> {
  return Object.fromEntries(items.map((item) => [item.id, item]));
}

function getCriteriaCountry(criteria: SearchCriteria): Country | null {
  const { destination } = criteria;

  return destination && destination.kind !== 'none' ? destination.country : null;
}

function byPrice<P extends Product>(left: P, right: P): number {
  return left.price - right.price;
}

/** Один запуск поиска: держит своё состояние и ведёт фазы; события уходят через эмиттер владельца. */
export default class SearchRun<P extends Product, DTO extends ProductDTO> {
  readonly searchUuid: string;
  readonly mode: ProductSearchMode;
  readonly progress: ProductSearchProgress = { jobsCount: 0, jobsSuccess: 0, operators: {} };
  readonly timings: ProductSearchTimings = { startedAt: null, firstResultsAt: null, finishedAt: null };
  state: ProductSearchState = 'idle';
  result: ProductSearchResult<P> | null = null;

  private readonly emitter: TypedEventEmitter<ProductSearchEvents<P>>;
  private readonly config: ProductSearchConfig<P, DTO>;
  private readonly criteria: SearchCriteria;
  private readonly options: ProductSearchOptions;
  private readonly request: ProductSearchRequest;
  private readonly endpoints: ProductSearchEndpoints;
  private readonly controller = new AbortController();
  private readonly dictionaries: ProductDictionaries;
  private readonly productsById = new Map<string, ProductEntry<P>>();
  private readonly hotelProducts = new Map<number, P[]>();
  private readonly hotelsById = new Map<number, HotelProduct>();
  private readonly countriesById = new Map<number, CountryProductDTO>();
  private readonly errors: ProductSearchError[] = [];
  private aborted = false;
  private completed = false;
  private timedOut = false;
  private statusPolled = false;
  private cacheReceived = false;
  private scanned = 0;
  private middleRequest: Promise<void> | null = null;
  private middleRequestPending = false;
  private requestMeta: SearchRequestMeta = { customerUuid: null, abTests: null };

  constructor({ emitter, config, criteria, options, searchUuid, clientUuid }: SearchRunParams<P, DTO>) {
    this.emitter = emitter;
    this.config = config;
    this.criteria = criteria;
    this.options = options;
    this.searchUuid = searchUuid;
    this.mode = options.mode ?? 'both';
    this.request = (config.buildRequest ?? buildProductSearchRequest)(criteria, {
      tracking: { commandUuid: searchUuid, clientUuid },
      params: options.params,
    });
    this.endpoints = config.resolveEndpoints(this.request);

    const country = getCriteriaCountry(criteria);
    this.dictionaries = {
      meals: indexById(MEALS),
      hotelCategories: indexById(HOTEL_CATEGORIES),
      departureCities: criteria.fromCity ? indexById<DepartureCity>([criteria.fromCity]) : {},
      countries: country ? indexById<Country>([country]) : {},
      resorts: indexById<Resort>(country?.resorts ?? []),
      hotels: {},
      firstPaymentDefinitions: {},
      operators: {},
    };
  }

  get products(): P[] {
    return Array.from(this.productsById.values(), (entry) => entry.product);
  }

  get hotels(): HotelProduct[] {
    return Array.from(this.hotelsById.values());
  }

  get productsByHotel(): Map<number, P[]> {
    return new Map(Array.from(this.hotelProducts, ([hotelId, products]) => [hotelId, [...products]]));
  }

  async execute({ withLowLimit }: { withLowLimit?: boolean } = {}): Promise<ProductSearchResult<P>> {
    this.state = 'loading';
    this.timings.startedAt = Date.now();
    this.emit('start', { searchUuid: this.searchUuid, criteria: this.criteria, options: this.options });
    if (this.aborted) return this.finish();

    if (!this.request.cache.country) {
      this.fail('criteria', new Error('ProductSearch: в критериях поиска не выбрана страна'));
      return this.finish();
    }

    this.requestMeta = await this.resolveRequestMeta();
    if (this.aborted) return this.finish();

    const hasSyncPhase = SYNC_MODES.has(this.mode);
    const hasAsyncPhase = ASYNC_MODES.has(this.mode);
    const getSyncPhase = (limit?: number) => {
      return hasSyncPhase ? this.runSyncPhase(hasAsyncPhase, limit) : Promise.resolve();
    };
    try {
      const syncPhase = getSyncPhase();
      const asyncPhase = hasAsyncPhase ? this.runAsyncPhase(syncPhase) : Promise.resolve();
      await Promise.all([withLowLimit ? getSyncPhase(3) : Promise.resolve(), syncPhase, asyncPhase]);
    } catch (error) {
      // Сюда попадают только программные ошибки (например, из createProduct): сетевые фазы ловят свои ошибки сами.
      if (!this.aborted) this.fail('run', error);
    } finally {
      this.controller.abort();
    }

    return this.finish();
  }

  abort(): void {
    if (this.result) return;

    this.aborted = true;
    this.state = 'aborted';
    this.timings.finishedAt = Date.now();
    this.controller.abort();
    this.result = this.buildResult('aborted');
    this.emit('end', this.result);
  }

  // Слушатель — код хоста: его исключение не должно менять исход поиска, а после end событий уже нет.
  private emit<K extends keyof ProductSearchEvents<P>>(event: K, payload: ProductSearchEvents<P>[K]): void {
    if (this.result && event !== 'end') return;

    try {
      this.emitter.emit(event, ...([payload] as EmitArgs<ProductSearchEvents<P>[K]>));
    } catch (error) {
      queueMicrotask(() => {
        throw error;
      });
    }
  }

  private finish(): ProductSearchResult<P> {
    if (this.result) return this.result;

    this.state = 'finished';
    this.timings.finishedAt = Date.now();
    this.result = this.buildResult('finished');
    this.emit('end', this.result);

    return this.result;
  }

  private async runSyncPhase(hasAsyncPhase: boolean, limit?: number): Promise<void> {
    try {
      const params = this.buildCacheParams(limit ?? this.options.limit ?? this.config.limits.sync);
      const result = await fetchProductSearchCache(this.endpoints.cache, params, this.controller.signal);
      if (this.aborted) return;
      this.applyBatch(result, 'sync');
      if (this.mode === 'sync') this.completed = true;
    } catch (error) {
      if (this.aborted || isAbortError(error)) return;
      // Сбой cache-выборки не отменяет асинхронную фазу — результаты придут из неё (паритет с монолитом).
      this.fail('sync', error);
    }

    if (this.aborted) return;
    if (hasAsyncPhase) this.state = 'asyncLoading';
    this.emit('syncEnd', this.snapshot());
  }

  private async runAsyncPhase(syncPhase: Promise<void>): Promise<void> {
    const session = await this.createSession();
    if (!session) return;

    // Статус сессии опрашивается только после ответа cache-выборки (паритет с afterFirstSearchRequest).
    await syncPhase;
    if (this.aborted) return;
    this.state = 'asyncLoading';

    const statusCompleted = await this.pollSessionStatus(session.id);
    this.statusPolled = true;
    while (this.middleRequest) await this.middleRequest;
    if (!statusCompleted || this.aborted) return;

    if (this.mode === 'asyncWithoutProducts') {
      this.completed = !this.timedOut;
      return;
    }

    try {
      const params = this.buildCacheParams(this.options.limit ?? this.config.limits.final, this.options.track);
      const result = await fetchProductSearchCache(this.endpoints.cache, params, this.controller.signal);
      if (this.aborted) return;
      this.scanned = this.config.pickProducts(result).length;
      this.applyBatch(result, 'final');
      this.completed = !this.timedOut;
    } catch (error) {
      if (!this.aborted && !isAbortError(error)) this.fail('final', error);
    }
  }

  private async resolveRequestMeta(): Promise<SearchRequestMeta> {
    const meta = { abTests: abTests.getCookie(), trSm: getTrSm() };
    if (this.options.customerUuid) return { ...meta, customerUuid: this.options.customerUuid };

    const customer = await getCustomerProfile();

    return { ...meta, customerUuid: customer?.uuid ?? null };
  }

  private async createSession(): Promise<ProductSearchSession | null> {
    const { customerUuid, abTests } = this.requestMeta;
    try {
      return await createProductSearchSession(
        this.endpoints.session,
        { ...this.request.session, customerUuid, abTests },
        this.controller.signal,
      );
    } catch (error) {
      if (!this.aborted && !isAbortError(error)) this.fail('session', error);
      return null;
    }
  }

  // Резолвится true, когда все job'ы завершены или вышло время ожидания; false — при прерывании поиска.
  private async pollSessionStatus(sessionId: string): Promise<boolean> {
    const parentSignal = this.controller.signal;
    const polling = new AbortController();
    const stopPolling = (): void => polling.abort();
    let waitingTimeIsUp = false;
    const waitingTimer = setTimeout(() => {
      waitingTimeIsUp = true;
      polling.abort();
    }, SEARCH_STATUS_WAITING_TIME_MS);

    parentSignal.addEventListener('abort', stopPolling, { once: true });
    try {
      while (!polling.signal.aborted) {
        const jobsCompleted = await this.requestSessionStatus(sessionId, polling.signal);
        if (jobsCompleted) return true;
        await sleep(SEARCH_STATUS_POLL_INTERVAL_MS, polling.signal);
      }
    } finally {
      clearTimeout(waitingTimer);
      parentSignal.removeEventListener('abort', stopPolling);
    }

    if (waitingTimeIsUp && !this.aborted) {
      // Время ожидания вышло — забираем всё, что успело найтись (как SessionStatus.js).
      this.timedOut = true;
      this.setProgress({ ...this.progress, jobsSuccess: this.progress.jobsCount });
      return true;
    }

    return false;
  }

  // Один опрос статуса; сетевая ошибка и ответ без success равнозначны — повтор через интервал (паритет с request.js).
  private async requestSessionStatus(sessionId: string, signal: AbortSignal): Promise<boolean> {
    const status = await getProductSearchSessionStatus(
      this.endpoints.session,
      { sessionId, searchUuid: this.searchUuid, abTests: this.requestMeta.abTests },
      signal,
    ).catch(() => null);
    if (!status || signal.aborted || !status.success) return false;

    const previousJobsSuccess = this.progress.jobsSuccess;
    this.setProgress(status);
    if (status.jobsCount === status.jobsSuccess) return true;
    if (status.jobsSuccess > previousJobsSuccess) this.startMiddleRequest();

    return false;
  }

  private startMiddleRequest(): void {
    if (this.aborted || this.statusPolled || this.mode === 'asyncWithoutProducts') return;

    // Один промежуточный запрос в полёте; рост jobsSuccess за это время даёт один догоняющий запрос.
    if (this.middleRequest) {
      this.middleRequestPending = true;
      return;
    }

    this.middleRequest = this.runMiddleRequest().finally(() => {
      this.middleRequest = null;
      if (this.middleRequestPending) {
        this.middleRequestPending = false;
        this.startMiddleRequest();
      }
    });
  }

  private async runMiddleRequest(): Promise<void> {
    try {
      const params = this.buildCacheParams(SEARCH_MIDDLE_REQUEST_LIMIT);
      const result = await fetchProductSearchCache(this.endpoints.cache, params, this.controller.signal);
      if (this.aborted) return;
      this.applyBatch(result, 'middle');
    } catch {
      // Промежуточная порция необязательна — финальная выборка всё равно придёт.
    }
  }

  private buildCacheParams(limit: number, track = false): ProductSearchCacheParams {
    const { customerUuid, abTests, trSm } = this.requestMeta;

    return {
      ...this.request.cache,
      customerUuid,
      ...(trSm && { trSm }),
      sections: this.config.sections,
      limit,
      ...(track && { track: 1 as const }),
      abTests,
    };
  }

  private applyBatch(result: ProductSearchCacheResult, phase: ProductSearchPhase): void {
    const newHotels = this.registerDictionaries(result);
    const newProducts: P[] = [];
    const replacedProductIds: string[] = [];
    const updatedHotelIds = new Set<number>();

    this.config.pickProducts(result).forEach((dto) => {
      const key = getProductKey(dto.id, this.config.getProductPrice?.(dto) ?? dto.price);
      const existing = this.productsById.get(dto.id);
      if (existing?.key === key) return;

      const product = this.config.createProduct(dto, this.dictionaries);
      this.productsById.set(dto.id, { key, product });
      newProducts.push(product);
      if (existing) {
        replacedProductIds.push(dto.id);
        this.detachFromHotel(existing.product, updatedHotelIds);
      }
      if (product.hotel) {
        this.attachToHotel(product);
        updatedHotelIds.add(product.hotel.id);
      }
    });
    updatedHotelIds.forEach((hotelId) => this.hotelProducts.get(hotelId)?.sort(byPrice));

    if (phase !== 'final' && this.hotelsById.size && this.timings.firstResultsAt === null) {
      this.timings.firstResultsAt = Date.now();
    }

    if (newProducts.length || newHotels.length) {
      const batch: ProductSearchBatch<P> = {
        phase,
        newProducts,
        newHotels,
        replacedProductIds,
        updatedHotelIds: Array.from(updatedHotelIds),
      };
      this.emit('found', batch);
    }

    if (!this.cacheReceived) {
      this.cacheReceived = true;
      this.emit('cache', this.snapshot());
    }
  }

  private registerDictionaries(result: ProductSearchCacheResult): HotelProduct[] {
    const newHotels: HotelProduct[] = [];
    result.hotels.forEach((hotelDto) => {
      if (this.hotelsById.has(hotelDto.id)) return;

      const hotel = new HotelProduct(hotelDto);
      this.hotelsById.set(hotelDto.id, hotel);
      this.dictionaries.hotels[hotelDto.id] = hotel;
      newHotels.push(hotel);
    });
    result.operators.forEach((operator) => {
      this.dictionaries.operators[operator.id] = operator;
    });
    result.firstPaymentDefinitions.forEach((definition) => {
      this.dictionaries.firstPaymentDefinitions[definition.id] = definition;
    });
    result.countries.forEach((country) => this.countriesById.set(country.id, country));

    return newHotels;
  }

  private attachToHotel(product: P): void {
    if (!product.hotel) return;

    const products = this.hotelProducts.get(product.hotel.id) ?? [];
    products.push(product);
    this.hotelProducts.set(product.hotel.id, products);
  }

  private detachFromHotel(product: P, updatedHotelIds: Set<number>): void {
    if (!product.hotel) return;

    const products = this.hotelProducts.get(product.hotel.id);
    const index = products?.indexOf(product) ?? -1;
    if (!products || index < 0) return;

    products.splice(index, 1);
    if (!products.length) this.hotelProducts.delete(product.hotel.id);
    updatedHotelIds.add(product.hotel.id);
  }

  private setProgress(status: Pick<ProductSearchSessionStatus, 'jobsCount' | 'jobsSuccess' | 'operators'>): void {
    this.progress.jobsCount = status.jobsCount;
    this.progress.jobsSuccess = status.jobsSuccess;
    this.progress.operators = status.operators;
    this.emit('progress', { ...this.progress });
  }

  private fail(phase: ProductSearchErrorPhase, error: unknown): void {
    const failure: ProductSearchError = { phase, error: toError(error) };
    this.errors.push(failure);
    this.emit('error', failure);
  }

  private snapshot(): ProductSearchSnapshot<P> {
    return {
      products: this.products,
      hotels: this.hotels,
      productsByHotel: this.productsByHotel,
      countries: Array.from(this.countriesById.values()),
      operators: Object.values(this.dictionaries.operators),
    };
  }

  private buildResult(state: ProductSearchResult<P>['state']): ProductSearchResult<P> {
    return {
      ...this.snapshot(),
      searchUuid: this.searchUuid,
      state,
      completed: this.completed,
      timedOut: this.timedOut,
      scanned: this.scanned,
      errors: [...this.errors],
      progress: { ...this.progress },
      timings: { ...this.timings },
    };
  }
}
